import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { useAuth } from "@/hooks/use-auth";
import { useLang } from "@/hooks/use-language";
import { useParentGate } from "@/hooks/use-parent-gate";
import { supabase } from "@/integrations/supabase/client";
import { JButton } from "@/components/JButton";
import { Confetti } from "@/components/Confetti";
import { toast } from "sonner";
import { z } from "zod";
import { hashPin, isValidPin } from "@/lib/pin";
import { CharacterPairPreview } from "@/components/CharacterPairPreview";
import { CharacterSays } from "@/components/CharacterSays";
import { pairForTier } from "@/lib/character-personality";
import { ArrivalSequence } from "@/components/onboarding/ArrivalSequence";
import { FirstMoment } from "@/components/onboarding/FirstMoment";

export const Route = createFileRoute("/_authenticated/onboarding")({
  component: Onboarding,
});

type Step = "arrival" | "welcome" | "parent" | "child" | "celebrate" | "firstMoment";

const AVATARS = [
  { e: "🌱", c: "primary" },
  { e: "🦊", c: "secondary" },
  { e: "🦉", c: "primary" },
  { e: "🌙", c: "secondary" },
  { e: "🌳", c: "primary" },
  { e: "🏮", c: "secondary" },
];

const TIERS = [
  { key: "4-6" as const, ar: "براعم", en: "Buds", sub: { en: "Ages 4–6", ar: "٤–٦ سنوات" }, emoji: "🌱", min: 4, max: 6 },
  { key: "7-9" as const, ar: "أغصان", en: "Branches", sub: { en: "Ages 7–9", ar: "٧–٩ سنوات" }, emoji: "🌿", min: 7, max: 9 },
  { key: "10-12" as const, ar: "جذور", en: "Roots", sub: { en: "Ages 10–12", ar: "١٠–١٢ سنة" }, emoji: "🌳", min: 10, max: 12 },
] as const;

type Tier = (typeof TIERS)[number];
type TierKey = Tier["key"];

function tierBullets(key: TierKey, isAr: boolean): string[] {
  if (key === "4-6") {
    return isAr
      ? ["قصص قصيرة بصور دافئة وأصوات لطيفة", "تلوين وألعاب لمسية بسيطة", "أوسمة جميلة تُحتفل بكل خطوة صغيرة", "بدون إعلانات ولا روابط خارجية — آمن تمامًا", "تقرير لطيف للوالدين كل أسبوع"]
      : ["Short stories with warm illustrations and gentle voice", "Simple tap-and-color activities", "Beautiful badges celebrating every tiny step", "Zero ads, zero outside links — fully safe", "A kind weekly note for parents"];
  }
  if (key === "7-9") {
    return isAr
      ? ["قصص ثنائية اللغة تنمّي الفهم والقيم", "تحديات يومية تبني سلسلة المثابرة", "ألعاب قراءة وكلمات تفاعلية", "نقاط وأوسمة تكافئ النمو الحقيقي", "لوحة والدين بتقدم أسبوعي واضح"]
      : ["Bilingual stories that grow comprehension and values", "Daily challenges that build streaks", "Interactive reading and word games", "Points and badges that reward real growth", "Clear weekly parent insights"];
  }
  return isAr
    ? ["قصص أعمق عن الهوية والحكمة والشجاعة", "تحديات تفكير ومحادثات بالعربية", "أوسمة جذور للحفاظ على اللغة والثقافة", "أدوات تأمل وتنفّس لإدارة المشاعر", "لوحة والدين بتقارير نمو أسبوعية"]
    : ["Deeper stories on identity, wisdom, and courage", "Thinking challenges and Arabic conversation prompts", "Roots badges that honor language and culture", "Mindful breathing and emotion tools", "Parent dashboard with weekly growth reports"];
}

function tierIntro(key: TierKey, isAr: boolean): string {
  if (key === "4-6") return isAr ? "عالم لطيف وآمن، مليء بالقصص الدافئة والألوان والاحتضان." : "A gentle, safe world full of warm stories, color, and cuddles.";
  if (key === "7-9") return isAr ? "رحلة استكشاف بالعربية والإنجليزية تنمو يومًا بعد يوم." : "An exploration journey in Arabic and English that grows day by day.";
  return isAr ? "تجربة أعمق تربط طفلك بجذوره وتُنمّي تفكيره ومشاعره." : "A deeper experience that connects your child to their roots and grows their mind.";
}

const childSchema = z.object({
  name: z.string().trim().min(1).max(40),
  age: z.number().int().min(4).max(12),
});

const STEPS: Step[] = ["arrival", "welcome", "parent", "child", "celebrate", "firstMoment"];

function Onboarding() {
  const { user } = useAuth();
  const { lang, dir, toggle } = useLang();
  const { refresh: refreshGate } = useParentGate();
  const navigate = useNavigate();

  const [step, setStep] = useState<Step>("arrival");
  const [newChildId, setNewChildId] = useState<string | null>(null);
  const [displayName, setDisplayName] = useState("");
  const [pin, setPin] = useState("");
  const [pinConfirm, setPinConfirm] = useState("");
  const [childName, setChildName] = useState("");
  const [tier, setTier] = useState<Tier>(TIERS[0]);
  const [age, setAge] = useState(5);
  const [avatar, setAvatar] = useState(AVATARS[0].e);
  const [busy, setBusy] = useState(false);

  const isAr = lang === "ar";
  const stepIndex = STEPS.indexOf(step);

  useEffect(() => {
    if (!user) return;
    supabase
      .from("profiles")
      .select("display_name, onboarded")
      .eq("id", user.id)
      .single()
      .then(({ data }) => {
        if (data?.onboarded) navigate({ to: "/app" });
        if (data?.display_name) setDisplayName(data.display_name);
      });
  }, [user, navigate]);

  useEffect(() => setAge(tier.min), [tier]);

  const finish = async () => {
    if (!user) return;
    const parsed = childSchema.safeParse({ name: childName, age });
    if (!parsed.success) return toast.error(parsed.error.issues[0].message);

    setBusy(true);
    const pinHash = pin ? await hashPin(pin, user.id) : null;

    const { error: profErr } = await supabase
      .from("profiles")
      .update({
        display_name: displayName.trim() || "Parent",
        onboarded: true,
        locale: lang,
        ...(pinHash ? { parent_pin_hash: pinHash, parent_pin_set_at: new Date().toISOString() } : {}),
      })
      .eq("id", user.id);
    if (profErr) {
      setBusy(false);
      return toast.error(profErr.message);
    }
    const { error: childErr, data: childData } = await supabase.from("children").insert({
      parent_id: user.id,
      name: parsed.data.name,
      age: parsed.data.age,
      age_tier: tier.key,
      avatar_emoji: avatar,
    }).select("id").single();
    setBusy(false);
    if (childErr) return toast.error(childErr.message);
    await refreshGate();
    // Quiet handoff: no toast, no celebration sound — that already happened.
    const id = (childData as { id: string } | null)?.id ?? null;
    setNewChildId(id);
    setStep("firstMoment");
  };

  const enterChildWorld = () => {
    if (newChildId) navigate({ to: "/app/child/$childId", params: { childId: newChildId } });
    else navigate({ to: "/app" });
  };

  const T = useMemo(
    () => ({
      langSwitch: isAr ? "EN" : "ع",
      back: isAr ? "رجوع" : "Back",
      next: isAr ? "متابعة" : "Continue",
      skip: isAr ? "تخطي" : "Skip for now",
      welcomeKicker: isAr ? "أهلاً بكم" : "Welcome",
      welcomeTitleAr: "جذوري",
      welcomeTitleEn: "Jozori",
      welcomeBody: isAr
        ? "أهلاً بكم في عالم دافئ وآمن لأطفالكم. خذوا وقتكم — لا شيء يستعجلكم هنا."
        : "Welcome to a warm, safe world for your child. Take your time — nothing is in a rush here.",
      start: isAr ? "هيا نبدأ" : "Let's begin",
      parentTitle: isAr ? "تعرّفنا بك" : "Tell us your name",
      parentSub: isAr
        ? "سنحييك كل صباح باسمك الجميل."
        : "We'll greet you with it every morning.",
      parentPlaceholder: isAr ? "مثال: ليلى" : "e.g. Layla",
      pinTitle: isAr ? "حماية منطقة الوالدين" : "Lock the parent zone",
      pinSub: isAr
        ? "اختر رقمًا سريًا من 4 أرقام يحمي الإعدادات والاشتراك من فضول الأطفال."
        : "Pick a 4-digit PIN that keeps settings and billing safe from curious little fingers.",
      pinEnter: isAr ? "أدخل الرمز" : "Enter PIN",
      pinConfirm: isAr ? "أعد الإدخال" : "Confirm PIN",
      pinMismatch: isAr ? "الرمزان غير متطابقين" : "PINs don't match",
      pinInvalid: isAr ? "الرمز يجب أن يكون 4 أرقام" : "PIN must be 4 digits",
      childTitle: isAr ? "أضف طفلك الأول" : "Add your first child",
      childSub: isAr
        ? "ستفتح له عالمًا مخصصًا لعمره. يمكنك إضافة المزيد لاحقاً."
        : "We'll open a world tailored to their age. Add more anytime.",
      childName: isAr ? "اسم الطفل" : "Child's name",
      childPlaceholder: isAr ? "مثال: آدم" : "e.g. Adam",
      ageGroup: isAr ? "الفئة العمرية" : "Age group",
      ageLabel: isAr ? "العمر" : "Age",
      pickAvatar: isAr ? "اختر شخصية" : "Pick a friend",
      meet: isAr ? "تعرّف على" : "Meet",
      ready: isAr
        ? "سيبدأ رحلته في عالم"
        : "will begin their journey in the",
      world: isAr ? "" : "world",
      included: isAr ? "ما يحصل عليه" : "What's included",
      bullets: tierBullets(tier.key, isAr),
      tierIntro: tierIntro(tier.key, isAr),
      finishCta: isAr ? "ابدأ الرحلة" : "Start the journey",
      finishBusy: isAr ? "نُجهّز كل شيء…" : "Setting up the magic…",
    }),
    [isAr, tier],
  );

  return (
    <div dir={dir} className="min-h-dvh bg-background relative overflow-hidden">
      {/* Magical floating background — drifting orbs + grain */}
      <div className="pointer-events-none absolute inset-0 -z-10">
        <div className="absolute -top-24 -left-20 size-72 rounded-full bg-primary/25 blur-3xl animate-orb-drift" />
        <div
          className="absolute top-1/3 -right-24 size-80 rounded-full bg-secondary/20 blur-3xl animate-orb-drift"
          style={{ animationDelay: "3s" }}
        />
        <div
          className="absolute -bottom-24 left-1/4 size-72 rounded-full bg-accent/25 blur-3xl animate-orb-drift"
          style={{ animationDelay: "6s" }}
        />
        {/* Subtle twinkle stars */}
        {[
          { top: "12%", left: "18%", d: "0s" },
          { top: "22%", left: "78%", d: "0.8s" },
          { top: "62%", left: "12%", d: "1.4s" },
          { top: "78%", left: "82%", d: "2.1s" },
          { top: "44%", left: "50%", d: "1.1s" },
        ].map((s, i) => (
          <span
            key={i}
            style={{ top: s.top, left: s.left, animationDelay: s.d }}
            className="absolute text-primary/40 text-xs animate-sparkle"
          >
            ✦
          </span>
        ))}
      </div>

      {/* Celebrate confetti — only on the celebrate beat, never on first-moment */}
      {step === "celebrate" && <Confetti />}

      {/* Chrome (top bar + progress) is hidden during ceremonial beats so the
          arrival and first-moment feel like thresholds, not form pages. */}
      {step !== "arrival" && step !== "firstMoment" && (
        <>
          <div className="px-5 pt-5 flex items-center justify-between max-w-xl mx-auto w-full">
            <button
              onClick={() => {
                const i = stepIndex;
                if (i > 0) setStep(STEPS[i - 1]);
              }}
              disabled={stepIndex <= 1}
              className="text-sm font-bold text-foreground/60 hover:text-foreground disabled:opacity-0 transition-all hover:-translate-x-0.5 rtl:hover:translate-x-0.5"
            >
              <span className="rtl:hidden">←</span>
              <span className="hidden rtl:inline">→</span> {T.back}
            </button>
            <button
              onClick={toggle}
              aria-label="Toggle language"
              className="h-9 min-w-9 px-3 rounded-full bg-card border border-clay/40 hover:bg-clay/30 text-xs font-bold transition-all hover:scale-105 active:scale-95 shadow-sm"
            >
              {T.langSwitch}
            </button>
          </div>

          <div className="px-5 pt-5 max-w-xl mx-auto">
            <div className="relative h-1.5 rounded-full bg-clay/30 overflow-hidden">
              <div
                className="absolute inset-y-0 left-0 rtl:left-auto rtl:right-0 rounded-full bg-gradient-to-r from-primary via-accent to-secondary transition-[width] duration-700 ease-[cubic-bezier(0.19,1,0.22,1)]"
                style={{ width: `${(stepIndex / (STEPS.length - 1)) * 100}%` }}
              >
                <div className="absolute inset-0 animate-shimmer rounded-full" />
              </div>
            </div>
            <div className="mt-2 flex justify-between text-[10px] font-bold uppercase tracking-widest text-foreground/40">
              <span>{`0${stepIndex}`.slice(-2)}</span>
              <span>{`0${STEPS.length - 2}`.slice(-2)}</span>
            </div>
          </div>
        </>
      )}


      <main className="px-5 pt-8 pb-12 max-w-xl mx-auto">
        {step === "arrival" && (
          <ArrivalSequence isAr={isAr} onEnter={() => setStep("welcome")} />
        )}

        {step === "firstMoment" && (
          <FirstMoment isAr={isAr} childName={childName} onEnter={enterChildWorld} />
        )}

        {step === "welcome" && (
          <div key="welcome" className="text-center animate-step-in">
            <Mascot mood="wave" />
            <p className="font-display-en text-xs uppercase tracking-[0.3em] text-primary font-bold mb-3 mt-4">
              {T.welcomeKicker}
            </p>
            <h1
              className={`${isAr ? "font-display-ar" : "font-display-en"} text-6xl md:text-7xl font-black mb-3 tracking-tight leading-none`}
            >
              {isAr ? T.welcomeTitleAr : T.welcomeTitleEn}
            </h1>
            <p className={`${isAr ? "font-display-ar" : "font-display-en"} text-xl text-foreground/60 mb-6`}>
              {isAr ? T.welcomeTitleEn : T.welcomeTitleAr}
            </p>
            <p className="text-base md:text-lg text-foreground/75 mb-10 max-w-md mx-auto leading-relaxed">
              {T.welcomeBody}
            </p>
            <JButton size="lg" className="w-full max-w-xs mx-auto" onClick={() => setStep("parent")}>
              ✨ {T.start}
            </JButton>
          </div>
        )}

        {step === "parent" && (
          <div key="parent" className="animate-step-in">
            <Mascot mood="smile" />
            <h1 className={`${isAr ? "font-display-ar" : "font-display-en"} text-3xl md:text-4xl font-bold mt-4 mb-2`}>
              {T.parentTitle}
            </h1>
            <p className="text-foreground/70 mb-8">{T.parentSub}</p>
            <input
              autoFocus
              type="text"
              dir={dir}
              placeholder={T.parentPlaceholder}
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              maxLength={40}
              className="w-full px-5 py-4 rounded-2xl border-2 border-clay/40 bg-card focus:border-primary focus:outline-none text-lg shadow-sm"
            />
            <JButton
              size="lg"
              className="w-full mt-8"
              onClick={() => setStep("child")}
              disabled={!displayName.trim()}
            >
              {T.next} →
            </JButton>
          </div>
        )}

        {/* PIN step deferred to just-in-time — set on first open of the parent area. */}


        {step === "child" && (
          <div key="child" className="animate-step-in">
            <Mascot mood="heart" />
            <h1 className={`${isAr ? "font-display-ar" : "font-display-en"} text-3xl md:text-4xl font-bold mt-4 mb-2`}>
              {T.childTitle}
            </h1>
            <p className="text-foreground/70 mb-7">{T.childSub}</p>

            <label className="block text-sm font-bold mb-2">{T.childName}</label>
            <input
              type="text"
              dir={dir}
              placeholder={T.childPlaceholder}
              value={childName}
              onChange={(e) => setChildName(e.target.value)}
              maxLength={40}
              className="w-full px-5 py-4 rounded-2xl border-2 border-clay/40 bg-card focus:border-primary focus:outline-none text-lg mb-6 shadow-sm"
            />

            <label className="block text-sm font-bold mb-3">{T.ageGroup}</label>
            <div className="grid grid-cols-3 gap-3 mb-6">
              {TIERS.map((t) => (
                <button
                  key={t.key}
                  onClick={() => setTier(t)}
                  className={`p-4 rounded-2xl border-2 text-center transition-all duration-200 ${
                    tier.key === t.key
                      ? "border-primary bg-primary/10 scale-105 shadow-lg"
                      : "border-clay/40 bg-card hover:border-clay"
                  }`}
                >
                  <div className="text-3xl mb-1">{t.emoji}</div>
                  <div className={`${isAr ? "font-display-ar" : "font-display-en"} text-base font-bold text-primary`}>
                    {isAr ? t.ar : t.en}
                  </div>
                  <div className="text-[10px] font-bold text-foreground/60 mt-0.5">{isAr ? t.sub.ar : t.sub.en}</div>
                </button>
              ))}
            </div>

            <label className="block text-sm font-bold mb-2">
              {T.ageLabel}: <span className="text-primary">{age}</span>
            </label>
            <input
              type="range"
              min={tier.min}
              max={tier.max}
              value={age}
              onChange={(e) => setAge(Number(e.target.value))}
              className="w-full accent-primary mb-6"
            />

            <label className="block text-sm font-bold mb-3">{T.pickAvatar}</label>
            <div className="grid grid-cols-6 gap-2.5 mb-8">
              {AVATARS.map((a) => (
                <button
                  key={a.e}
                  onClick={() => setAvatar(a.e)}
                  className={`aspect-square text-3xl rounded-2xl border-2 flex items-center justify-center transition-all duration-200 ${
                    avatar === a.e
                      ? "border-primary bg-primary/15 scale-110 shadow-md"
                      : "border-clay/40 bg-card hover:border-clay/70"
                  }`}
                >
                  {a.e}
                </button>
              ))}
            </div>

            <div className="mb-8">
              <CharacterPairPreview pair={pairForTier(tier.key)} isAr={isAr} />
            </div>

            <JButton size="lg" className="w-full" onClick={() => setStep("celebrate")} disabled={!childName.trim()}>
              {T.next} →
            </JButton>
          </div>
        )}

        {step === "celebrate" && (
          <div key="celebrate" className="text-center animate-step-in">
            <div className="relative inline-block mb-6">
              <div className="absolute inset-0 -m-8 rounded-full bg-gradient-to-br from-primary/30 via-accent/30 to-secondary/30 blur-3xl animate-breathe" />
              <div
                className="relative text-8xl"
                style={{ animation: "float 3s ease-in-out infinite, scale-in 0.6s cubic-bezier(0.34, 1.56, 0.64, 1) both" }}
              >
                {avatar}
              </div>
              {/* Orbiting sparkles */}
              {["✨", "⭐", "🌟", "💫"].map((s, i) => (
                <span
                  key={i}
                  className="absolute text-xl animate-sparkle"
                  style={{
                    top: `${20 + Math.sin((i / 4) * Math.PI * 2) * 50}%`,
                    left: `${50 + Math.cos((i / 4) * Math.PI * 2) * 60}%`,
                    animationDelay: `${i * 0.25}s`,
                  }}
                >
                  {s}
                </span>
              ))}
            </div>
            <p className="font-display-en text-xs uppercase tracking-[0.3em] text-primary font-bold mb-2">
              {T.meet}
            </p>
            <h1 className={`${isAr ? "font-display-ar" : "font-display-en"} text-4xl md:text-5xl font-bold mb-3 tracking-tight`}>
              {childName}
            </h1>
            <p className="text-foreground/70 mb-3 max-w-sm mx-auto">
              {childName} {T.ready}{" "}
              <span className={`${isAr ? "font-display-ar" : "font-display-en"} font-bold text-primary`}>
                {isAr ? tier.ar : tier.en}
              </span>
              {T.world ? ` ${T.world}` : ""}.
            </p>
            <p className={`${isAr ? "ar-story" : ""} text-sm text-foreground/60 mb-8 max-w-sm mx-auto italic`}>
              {T.tierIntro}
            </p>
            <div className="bg-card border-2 border-clay/30 rounded-3xl p-5 mb-5 text-start shadow-lg">
              <h3 className="font-bold mb-3 text-xs uppercase tracking-widest text-foreground/60">
                {T.included}
              </h3>
              <ul className="space-y-2 text-sm">
                {T.bullets.map((b: string, i: number) => (
                  <li key={i} className="flex items-start gap-2">
                    <span className="text-primary font-bold">✓</span>
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
            </div>
            {(() => {
              const [first] = pairForTier(tier.key);
              const hi = isAr ? first.hiAr : first.hiEn;
              const intro = isAr
                ? `أهلًا ${childName || "صديقي"}! ${hi}`
                : `Hi ${childName || "friend"}! ${hi}`;
              return (
                <div className="mb-8 text-start">
                  <CharacterSays
                    persona={first}
                    isAr={isAr}
                    message={intro}
                    verbEn="welcomes you"
                    verbAr="يرحّب بك"
                    linkProfile={false}
                  />
                </div>
              );
            })()}
            <JButton size="lg" className="w-full relative overflow-hidden" onClick={finish} disabled={busy}>
              {busy ? (
                <span className="inline-flex items-center gap-2">
                  <span
                    className="inline-block size-4 rounded-full border-2 border-primary-foreground/40 border-t-primary-foreground"
                    style={{ animation: "spin 0.8s linear infinite" }}
                  />
                  {T.finishBusy}
                </span>
              ) : (
                <>🌟 {T.finishCta}</>
              )}
              {busy && <span className="absolute inset-0 animate-shimmer pointer-events-none" />}
            </JButton>
          </div>
        )}
      </main>
    </div>
  );
}

function PinField({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <div>
      <label className="block text-sm font-bold mb-2">{label}</label>
      <div className="relative group">
        <input
          type="password"
          inputMode="numeric"
          pattern="\d*"
          maxLength={4}
          value={value}
          onChange={(e) => onChange(e.target.value.replace(/\D/g, "").slice(0, 4))}
          className="w-full px-5 py-4 rounded-2xl border-2 border-clay/40 bg-card focus:border-primary focus:outline-none text-2xl tracking-[1em] text-center font-bold shadow-sm transition-all focus:shadow-[0_0_0_4px_oklch(0.62_0.17_45/0.15)]"
          placeholder="••••"
          aria-label={label}
        />
        {/* Filled dots indicator */}
        <div className="absolute inset-x-0 -bottom-3 flex justify-center gap-1.5 pointer-events-none">
          {[0, 1, 2, 3].map((i) => (
            <span
              key={i}
              className={`h-1 w-6 rounded-full transition-all duration-300 ${
                i < value.length ? "bg-primary scale-100" : "bg-clay/40 scale-90"
              }`}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

function Mascot({ mood }: { mood: "wave" | "smile" | "shield" | "heart" }) {
  const emoji = mood === "wave" ? "🦊" : mood === "smile" ? "🦉" : mood === "shield" ? "🛡️" : "💝";
  return (
    <div className="relative mx-auto w-28 h-28">
      {/* Soft breathing halo */}
      <div className="absolute inset-0 rounded-full bg-gradient-to-br from-primary/40 via-accent/30 to-secondary/30 blur-2xl animate-breathe" />
      {/* Rotating sparkle ring */}
      <div
        className="absolute -inset-2 rounded-full opacity-60"
        style={{
          background:
            "conic-gradient(from 0deg, transparent 0deg, oklch(0.7 0.15 55 / 0.4) 60deg, transparent 120deg, transparent 240deg, oklch(0.62 0.17 45 / 0.4) 300deg, transparent 360deg)",
          animation: "spin 8s linear infinite",
          maskImage: "radial-gradient(circle, transparent 56%, black 58%, black 68%, transparent 70%)",
          WebkitMaskImage:
            "radial-gradient(circle, transparent 56%, black 58%, black 68%, transparent 70%)",
        }}
      />
      <div
        className="relative w-full h-full rounded-full bg-card border-2 border-clay/40 shadow-xl flex items-center justify-center text-5xl animate-float"
      >
        {emoji}
      </div>
      {/* Floating sparkles */}
      <span className="absolute -top-1 -right-1 text-sm animate-sparkle">✨</span>
      <span
        className="absolute -bottom-1 -left-1 text-xs animate-sparkle"
        style={{ animationDelay: "1.1s" }}
      >
        ⭐
      </span>
    </div>
  );
}
